import { useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

/**
 * BirthdayLoginToast — fires a one-off Sonner celebration when the
 * logged-in user's birth_date matches today (UK time).
 *
 * Rules:
 * - Only shows for the logged-in user themselves (not admins looking at
 *   other people's birthdays — that belongs to BirthdayBanner).
 * - Fires once per user per calendar day via localStorage
 *   (`cc_bday_toast_seen_<user_id>_<YYYY-MM-DD>`), so refreshing the
 *   page or navigating around doesn't re-toast.
 * - Silent when user isn't logged in, has no birth_date, or the date
 *   doesn't match today.
 *
 * Mounted globally in App.js so the toast fires no matter which page
 * the user lands on after login.
 */
export default function BirthdayLoginToast() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading || !user) return;
    const bd = user.birth_date;
    if (!bd || typeof bd !== "string" || bd.length < 5) return;

    // birth_date is stored as YYYY-MM-DD; match by MM-DD in UK time.
    // Using en-GB locale + Europe/London TZ so we don't fire the toast
    // early for users landing at 23:30 UTC on their birthday-eve.
    let todayMD;
    try {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/London", month: "2-digit", day: "2-digit",
      }).formatToParts(new Date());
      const mm = parts.find((p) => p.type === "month")?.value || "";
      const dd = parts.find((p) => p.type === "day")?.value || "";
      todayMD = `${mm}-${dd}`;
    } catch {
      const d = new Date();
      todayMD = `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    }

    const bdMD = bd.slice(5, 10); // "MM-DD"
    if (bdMD !== todayMD) return;

    const todayStamp = new Date().toISOString().slice(0, 10);
    const uid = user.user_id || user.id || user.email || "anon";
    const seenKey = `cc_bday_toast_seen_${uid}_${todayStamp}`;
    try {
      if (localStorage.getItem(seenKey)) return;
    } catch { /* noop */ }

    // Fire the celebration a beat after mount so it doesn't collide
    // with the login redirect / other post-login toasts.
    const timeout = setTimeout(() => {
      const firstName = ((user.display_name || user.name || user.email || "").split(" ")[0] || "camper").slice(0, 40);
      toast.success(
        `🎂 Happy birthday, ${firstName}!`,
        {
          description: "Your friends can see your birthday shout-out on the Community page today. Have a cracking one 🥳",
          duration: 12000,
          action: {
            label: "See it",
            onClick: () => { try { window.location.assign("/community"); } catch { /* noop */ } },
          },
        },
      );
      try { localStorage.setItem(seenKey, "1"); } catch { /* noop */ }
    }, 900);

    return () => clearTimeout(timeout);
  }, [user, loading]);

  return null;
}
